import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import ValidationRefundNotice from "@/components/ValidationRefundNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Plus, X } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";

const EMPTY = {
  practice_areas: { area: "", level: "principal" },
  publications: { title: "", year: "", source: "", url: "" },
  notable_decisions: { matter: "", jurisdiction: "", year: "", summary: "" },
  thematic_analyses: { title: "", summary: "", year: "", url: "" },
  professional_involvements: { role: "", organization: "", year: "" },
};

export default function ExpertApply() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    profession: "", company: "", description: "", city: "",
    languages: ["Français"], rates: "", consultation_price: "", experience_years: "",
    linkedin: "", website: "", photo: "",
    specialties: [],
    practice_areas: [],
    publications: [],
    notable_decisions: [],
    thematic_analyses: [],
    professional_involvements: [],
  });
  const [loading, setLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addRow = (key) => setForm(f => ({ ...f, [key]: [...f[key], { ...EMPTY[key] }] }));
  const updateRow = (key, idx, field, value) => setForm(f => ({
    ...f, [key]: f[key].map((r, i) => i === idx ? { ...r, [field]: value } : r),
  }));
  const removeRow = (key, idx) => setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const runAIStructure = async () => {
    if (!aiText || aiText.trim().length < 80) {
      toast.error("Collez votre biographie ou CV (au moins 80 caractères) pour lancer l'analyse.");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await apiClient.post("/ai/structure-profile", { text: aiText });
      setForm(f => ({
        ...f,
        practice_areas: data.practice_areas?.length ? data.practice_areas : f.practice_areas,
        publications: data.publications?.length ? data.publications : f.publications,
        notable_decisions: data.notable_decisions?.length ? data.notable_decisions : f.notable_decisions,
        thematic_analyses: data.thematic_analyses?.length ? data.thematic_analyses : f.thematic_analyses,
        professional_involvements: data.professional_involvements?.length ? data.professional_involvements : f.professional_involvements,
      }));
      toast.success("Profil structuré — vérifiez et complétez chaque section.");
    } catch (e) {
      toast.error("L'analyse documentaire n'a pas pu aboutir.");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    apiClient.get("/categories").then(r => setCats(r.data));
    apiClient.get("/experts/me").then(r => {
      const e = r.data;
      setForm({
        profession: e.profession || "", company: e.company || "",
        description: e.description || "", city: e.city || "",
        languages: e.languages || ["Français"], rates: e.rates || "",
        consultation_price: e.consultation_price ?? "",
        experience_years: e.experience_years || "",
        linkedin: e.linkedin || "", website: e.website || "", photo: e.photo || "",
        specialties: e.specialties || [],
        practice_areas: e.practice_areas || [],
        publications: e.publications || [],
        notable_decisions: e.notable_decisions || [],
        thematic_analyses: e.thematic_analyses || [],
        professional_involvements: e.professional_involvements || [],
      });
    }).catch(() => {});
  }, []);

  const toggleSpec = (id) => {
    setForm(f => ({ ...f, specialties: f.specialties.includes(id) ? f.specialties.filter(s => s !== id) : [...f.specialties, id] }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const cleanList = (list, requiredKey) =>
      (list || []).filter(r => r && (r[requiredKey] || "").toString().trim());
    try {
      const body = {
        ...form,
        experience_years: form.experience_years ? Number(form.experience_years) : null,
        consultation_price: form.consultation_price ? Number(form.consultation_price) : null,
        practice_areas: cleanList(form.practice_areas, "area"),
        publications: cleanList(form.publications, "title").map(p => ({ ...p, year: p.year ? Number(p.year) : null })),
        notable_decisions: cleanList(form.notable_decisions, "matter").map(d => ({ ...d, year: d.year ? Number(d.year) : null })),
        thematic_analyses: cleanList(form.thematic_analyses, "title").map(a => ({ ...a, year: a.year ? Number(a.year) : null })),
        professional_involvements: cleanList(form.professional_involvements, "role").map(p => ({ ...p, year: p.year ? Number(p.year) : null })),
      };
      await apiClient.post("/experts/apply", body);
      toast.success("Candidature transmise — en cours de validation par CONFILEX");
      nav("/expert/dashboard");
    } catch (err) {
      toast.error("La transmission a échoué");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Adhésion spécialiste</div>
        <h1 className="font-serif text-4xl text-[#0B3A2A] tracking-tight mb-2">Rejoindre les spécialistes référencés CONFILEX</h1>
        <p className="text-slate-600 mb-6">Rejoignez le réseau sélectif de spécialistes référencés par CONFILEX. Votre profil sera examiné avant publication.</p>

        <ValidationRefundNotice className="mb-8" />

        <form onSubmit={onSubmit} className="space-y-6" data-testid="expert-apply-form">
          <Card className="p-6 lg:p-8 rounded-2xl border-slate-100 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Profession</Label>
                <Input required value={form.profession} onChange={e => set("profession", e.target.value)} placeholder="ex: Avocat, Expert-comptable..." data-testid="apply-profession" className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Cabinet / Société</Label>
                <Input value={form.company} onChange={e => set("company", e.target.value)} className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Ville</Label>
                <CityAutocomplete
                  value={form.city}
                  onChange={(name) => set("city", name)}
                  placeholder="Saisir votre ville d'exercice"
                  inputClassName="h-11 rounded-xl border-slate-200 bg-white"
                  data-testid="apply-city"
                />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Années d'expérience</Label>
                <Input type="number" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Honoraires indicatifs</Label>
                <Input value={form.rates} onChange={e => set("rates", e.target.value)} placeholder="ex : 200 €/h" className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Prix consultation en ligne (€)</Label>
                <Input type="number" min="0" step="10" value={form.consultation_price} onChange={e => set("consultation_price", e.target.value)} placeholder="ex : 150" data-testid="apply-consultation-price" className="h-11 rounded-xl border-slate-200" />
                <p className="text-xs text-slate-500 mt-1">Activez la réservation directe en saisissant un prix. Une commission CONFILEX de 15 % est appliquée.</p>
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Langues</Label>
                <Input value={form.languages.join(", ")} onChange={e => set("languages", e.target.value.split(",").map(x => x.trim()).filter(Boolean))} className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">LinkedIn</Label>
                <Input value={form.linkedin} onChange={e => set("linkedin", e.target.value)} className="h-11 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Site web</Label>
                <Input value={form.website} onChange={e => set("website", e.target.value)} className="h-11 rounded-xl border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-[#0B3A2A] mb-2 block">URL photo professionnelle</Label>
              <Input value={form.photo} onChange={e => set("photo", e.target.value)} placeholder="https://..." className="h-11 rounded-xl border-slate-200" />
            </div>
            <div>
              <Label className="text-[#0B3A2A] mb-2 block">Présentation / biographie</Label>
              <Textarea required rows={5} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Vos compétences, votre approche, vos succès..." className="rounded-xl border-slate-200" />
            </div>
          </Card>

          <Card className="p-6 lg:p-8 rounded-2xl border-slate-100">
            <Label className="text-[#0B3A2A] mb-3 block">Domaines de spécialité</Label>
            <p className="text-xs text-slate-500 mb-4">Sélectionnez les domaines dans lesquels vous intervenez à titre professionnel.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cats.map(c => (
                <label key={c.id} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-smooth ${form.specialties.includes(c.id) ? "border-[#C9A961] bg-[#C9A961]/10" : "border-slate-200 bg-white"}`}>
                  <Checkbox checked={form.specialties.includes(c.id)} onCheckedChange={() => toggleSpec(c.id)} />
                  <span className="text-sm text-[#0B3A2A]">{c.label}</span>
                </label>
              ))}
            </div>
          </Card>

          {/* PROFIL ENRICHI */}
          <Card className="p-6 lg:p-8 rounded-2xl border-[#C9A961]/30 bg-[#FAF6EE]/30" data-testid="enrichment-section">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Profil enrichi</div>
            <h3 className="font-serif text-2xl text-[#0B3A2A] mb-2">Analyse documentaire</h3>
            <p className="text-sm text-slate-600 mb-5">
              Collez votre biographie professionnelle ou votre CV. Notre dispositif d'analyse documentaire vous proposera une
              structuration de vos domaines d'intervention récurrents, publications, décisions notables (anonymisées) et engagements.
              Vous resterez maître de chaque entrée.
            </p>
            <Textarea
              rows={5}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Collez ici votre biographie professionnelle, votre CV ou votre fiche de cabinet…"
              className="rounded-xl border-slate-200 mb-3 bg-white"
              data-testid="ai-bio-input"
            />
            <Button type="button" onClick={runAIStructure} disabled={aiLoading || aiText.trim().length < 80} data-testid="ai-structure-btn"
              variant="outline" className="rounded-full border-[#C9A961]/40 text-[#0B3A2A] hover:bg-[#C9A961]/10 disabled:opacity-50 disabled:cursor-not-allowed">
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-[#C9A961]" />}
              {aiLoading ? "Analyse en cours…" : "Structurer mon profil"}
            </Button>

            <Accordion type="multiple" className="mt-6">
              {/* Domaines d'intervention */}
              <AccordionItem value="practice" className="border-slate-200">
                <AccordionTrigger className="text-[#0B3A2A] hover:no-underline">
                  Domaines d'intervention récurrents <span className="text-xs text-slate-500 ml-2">({form.practice_areas.length})</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  {form.practice_areas.map((row, idx) => (
                    <div key={`pa-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-7 h-10 rounded-lg border-slate-200 bg-white" placeholder="Ex : Restructuration d'entreprise" value={row.area} onChange={(e) => updateRow("practice_areas", idx, "area", e.target.value)} />
                      <select className="col-span-4 h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm" value={row.level} onChange={(e) => updateRow("practice_areas", idx, "level", e.target.value)}>
                        <option value="principal">Principal</option>
                        <option value="regulier">Régulier</option>
                        <option value="occasionnel">Occasionnel</option>
                      </select>
                      <button type="button" onClick={() => removeRow("practice_areas", idx)} className="col-span-1 text-slate-400 hover:text-[#8B3A3A]"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" onClick={() => addRow("practice_areas")} className="text-[#C9A961] hover:bg-[#C9A961]/10">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un domaine
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Publications */}
              <AccordionItem value="publications" className="border-slate-200">
                <AccordionTrigger className="text-[#0B3A2A] hover:no-underline">
                  Publications <span className="text-xs text-slate-500 ml-2">({form.publications.length})</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  {form.publications.map((row, idx) => (
                    <div key={`pub-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-5 h-10 rounded-lg border-slate-200 bg-white" placeholder="Titre" value={row.title} onChange={(e) => updateRow("publications", idx, "title", e.target.value)} />
                      <Input className="col-span-3 h-10 rounded-lg border-slate-200 bg-white" placeholder="Revue / éditeur" value={row.source} onChange={(e) => updateRow("publications", idx, "source", e.target.value)} />
                      <Input className="col-span-2 h-10 rounded-lg border-slate-200 bg-white" type="number" placeholder="Année" value={row.year} onChange={(e) => updateRow("publications", idx, "year", e.target.value)} />
                      <Input className="col-span-1 h-10 rounded-lg border-slate-200 bg-white" placeholder="URL" value={row.url} onChange={(e) => updateRow("publications", idx, "url", e.target.value)} />
                      <button type="button" onClick={() => removeRow("publications", idx)} className="col-span-1 text-slate-400 hover:text-[#8B3A3A]"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" onClick={() => addRow("publications")} className="text-[#C9A961] hover:bg-[#C9A961]/10">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une publication
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Décisions */}
              <AccordionItem value="decisions" className="border-slate-200">
                <AccordionTrigger className="text-[#0B3A2A] hover:no-underline">
                  Décisions notables (anonymisées) <span className="text-xs text-slate-500 ml-2">({form.notable_decisions.length})</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <p className="text-xs text-slate-500 italic">Aucune information identifiant les parties ne doit être saisie.</p>
                  {form.notable_decisions.map((row, idx) => (
                    <div key={`dec-${idx}`} className="space-y-2 p-3 rounded-lg border border-slate-200 bg-white">
                      <div className="grid grid-cols-12 gap-2">
                        <Input className="col-span-5 h-10 rounded-lg border-slate-200" placeholder="Matière" value={row.matter} onChange={(e) => updateRow("notable_decisions", idx, "matter", e.target.value)} />
                        <Input className="col-span-4 h-10 rounded-lg border-slate-200" placeholder="Juridiction" value={row.jurisdiction} onChange={(e) => updateRow("notable_decisions", idx, "jurisdiction", e.target.value)} />
                        <Input className="col-span-2 h-10 rounded-lg border-slate-200" type="number" placeholder="Année" value={row.year} onChange={(e) => updateRow("notable_decisions", idx, "year", e.target.value)} />
                        <button type="button" onClick={() => removeRow("notable_decisions", idx)} className="col-span-1 text-slate-400 hover:text-[#8B3A3A]"><X className="w-4 h-4" /></button>
                      </div>
                      <Textarea rows={2} className="rounded-lg border-slate-200" placeholder="Synthèse anonymisée…" value={row.summary} onChange={(e) => updateRow("notable_decisions", idx, "summary", e.target.value)} />
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" onClick={() => addRow("notable_decisions")} className="text-[#C9A961] hover:bg-[#C9A961]/10">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une décision
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Analyses thématiques */}
              <AccordionItem value="analyses" className="border-slate-200">
                <AccordionTrigger className="text-[#0B3A2A] hover:no-underline">
                  Analyses thématiques <span className="text-xs text-slate-500 ml-2">({form.thematic_analyses.length})</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  {form.thematic_analyses.map((row, idx) => (
                    <div key={`ana-${idx}`} className="space-y-2 p-3 rounded-lg border border-slate-200 bg-white">
                      <div className="grid grid-cols-12 gap-2">
                        <Input className="col-span-7 h-10 rounded-lg border-slate-200" placeholder="Titre" value={row.title} onChange={(e) => updateRow("thematic_analyses", idx, "title", e.target.value)} />
                        <Input className="col-span-2 h-10 rounded-lg border-slate-200" type="number" placeholder="Année" value={row.year} onChange={(e) => updateRow("thematic_analyses", idx, "year", e.target.value)} />
                        <Input className="col-span-2 h-10 rounded-lg border-slate-200" placeholder="URL" value={row.url} onChange={(e) => updateRow("thematic_analyses", idx, "url", e.target.value)} />
                        <button type="button" onClick={() => removeRow("thematic_analyses", idx)} className="col-span-1 text-slate-400 hover:text-[#8B3A3A]"><X className="w-4 h-4" /></button>
                      </div>
                      <Textarea rows={2} className="rounded-lg border-slate-200" placeholder="Résumé…" value={row.summary} onChange={(e) => updateRow("thematic_analyses", idx, "summary", e.target.value)} />
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" onClick={() => addRow("thematic_analyses")} className="text-[#C9A961] hover:bg-[#C9A961]/10">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une analyse
                  </Button>
                </AccordionContent>
              </AccordionItem>

              {/* Engagements professionnels */}
              <AccordionItem value="involvements" className="border-slate-200">
                <AccordionTrigger className="text-[#0B3A2A] hover:no-underline">
                  Engagements professionnels <span className="text-xs text-slate-500 ml-2">({form.professional_involvements.length})</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  {form.professional_involvements.map((row, idx) => (
                    <div key={`inv-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-5 h-10 rounded-lg border-slate-200 bg-white" placeholder="Rôle" value={row.role} onChange={(e) => updateRow("professional_involvements", idx, "role", e.target.value)} />
                      <Input className="col-span-4 h-10 rounded-lg border-slate-200 bg-white" placeholder="Organisation" value={row.organization} onChange={(e) => updateRow("professional_involvements", idx, "organization", e.target.value)} />
                      <Input className="col-span-2 h-10 rounded-lg border-slate-200 bg-white" type="number" placeholder="Année" value={row.year} onChange={(e) => updateRow("professional_involvements", idx, "year", e.target.value)} />
                      <button type="button" onClick={() => removeRow("professional_involvements", idx)} className="col-span-1 text-slate-400 hover:text-[#8B3A3A]"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" onClick={() => addRow("professional_involvements")} className="text-[#C9A961] hover:bg-[#C9A961]/10">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un engagement
                  </Button>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => nav(-1)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="apply-submit"
              className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold px-8 h-11">
              {loading ? "Transmission…" : "Soumettre ma candidature"}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
